import { useCommonMutation, useCommonQuery } from 'hooks';
import { LoginAPI, ReqLogin, ResLogin } from 'domains/Auth';
import { BaseApiResponse, ResultError } from 'modules';
import { ReqCreateUser } from '../models/create-user.model';
import { userKeys } from '../keys/user.key';
import { UserAPI } from '../apis/user.api';
import {
  IUser,
  IUserDetail,
  ReqUserDetail,
  ReqUsers,
  ResUserDetail,
  ResUsers
} from '../models/user.model';
import { ReqDeleteUser } from '../models/delete-user.model';
import { ReqUpdateUser, ReqUpdateUserPhoto } from '../models/update-user.model';

const userApi = new UserAPI();

export const useCreateUser = () => {
  return useCommonMutation<BaseApiResponse, ResultError, ReqCreateUser>({
    mutationKey: userKeys.CREATE_USER(),
    mutationFn: (data) => userApi.createUser(data),
    queryKeyToInvalidate: userKeys.USER_ALL()
  });
};

export const useUpdateUser = () => {
  return useCommonMutation<BaseApiResponse, ResultError, ReqUpdateUser>({
    mutationKey: userKeys.UPDATE_USER(),
    mutationFn: (data) => userApi.updateUser(data),
    queryKeyToInvalidate: userKeys.USER_ALL()
  });
};

export const useUpdateUserPhoto = () => {
  return useCommonMutation<BaseApiResponse, ResultError, ReqUpdateUserPhoto>({
    mutationKey: userKeys.UPDATE_USER_PHOTO(),
    mutationFn: (data) => userApi.updateUserPhoto(data),
    queryKeyToInvalidate: userKeys.USER_ALL()
  });
};

export const useFetchUsers = ({ data }: { data: ReqUsers }) => {
  return useCommonQuery<ReqUsers, ResUsers, ResultError, IUser[]>({
    data,
    queryFn: (data, pagination) => {
      const request = {
        ...data,
        page: pagination.current,
        size: pagination.pageSize
      };

      return userApi.fetchUsers(request);
    },
    queryKeyFactory: (data) => userKeys.FETCH_USERS(data),
    options: {
      select: (data) => data.resultList
    }
  });
};

export const useFetchUserDetail = ({ data }: { data: ReqUserDetail }) => {
  return useCommonQuery<ReqUserDetail, ResUserDetail, ResultError, IUserDetail>({
    data,
    queryFn: (data) => userApi.fetchUserDetail(data),
    queryKeyFactory: (data) => userKeys.FETCH_USER_DETAIL(data.userId),
    options: {
      select: (data) => data.resultObject
    }
  });
};

export const useDeleteUser = () => {
  return useCommonMutation<BaseApiResponse, ResultError, ReqDeleteUser>({
    mutationKey: userKeys.DELETE_USER(),
    mutationFn: (data) => userApi.deleteUser(data),
    queryKeyToInvalidate: userKeys.USER_ALL()
  });
};
